import React, { createContext, useContext, useState, useEffect } from 'react';

const PlatformThemeContext = createContext();

export const usePlatformTheme = () => {
  const context = useContext(PlatformThemeContext);
  if (!context) {
    throw new Error('usePlatformTheme must be used within a PlatformThemeProvider');
  }
  return context;
};

// Platform admin theme presets
const themePresets = {
  default: {
    name: 'Default Blue',
    colors: {
      primary: '#3B82F6',      // blue-500
      primaryHover: '#2563EB', // blue-600
      accent: '#8B5CF6',       // purple-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  },
  purple: {
    name: 'Purple Haze',
    colors: {
      primary: '#8B5CF6',      // purple-500
      primaryHover: '#7C3AED', // purple-600
      accent: '#EC4899',       // pink-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  },
  teal: {
    name: 'Teal Ocean',
    colors: {
      primary: '#14B8A6',      // teal-500
      primaryHover: '#0D9488', // teal-600
      accent: '#06B6D4',       // cyan-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  },
  indigo: {
    name: 'Indigo Night',
    colors: {
      primary: '#6366F1',      // indigo-500
      primaryHover: '#4F46E5', // indigo-600
      accent: '#8B5CF6',       // purple-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  },
  rose: {
    name: 'Rose Garden',
    colors: {
      primary: '#F43F5E',      // rose-500
      primaryHover: '#E11D48', // rose-600
      accent: '#EC4899',       // pink-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  },
  emerald: {
    name: 'Emerald Forest',
    colors: {
      primary: '#10B981',      // emerald-500
      primaryHover: '#059669', // emerald-600
      accent: '#14B8A6',       // teal-500
      success: '#10B981',      // green-500
      warning: '#F59E0B',      // amber-500
      danger: '#EF4444',       // red-500
    }
  }
};

export const PlatformThemeProvider = ({ children }) => {
  // Load saved preferences from localStorage
  const [darkMode, setDarkMode] = useState(() => {
    // Check both platform admin and global dark mode settings
    const platformDarkMode = localStorage.getItem('platformAdminDarkMode');
    const globalDarkMode = localStorage.getItem('darkMode');
    
    // If platform admin has a preference, use that; otherwise use global
    if (platformDarkMode !== null) {
      return JSON.parse(platformDarkMode);
    }
    return globalDarkMode === 'true';
  });

  const [colorScheme, setColorScheme] = useState(() => {
    const saved = localStorage.getItem('platformAdminColorScheme');
    return saved || 'default';
  });

  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem('platformAdminCompactMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('platformAdminSidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Apply dark mode to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('platformAdminDarkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Apply color scheme CSS variables
  useEffect(() => {
    const theme = themePresets[colorScheme] || themePresets.default;
    const root = document.documentElement;
    
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--platform-${key}`, value);
    });
    
    localStorage.setItem('platformAdminColorScheme', colorScheme);
  }, [colorScheme]);

  // Save compact mode preference
  useEffect(() => {
    localStorage.setItem('platformAdminCompactMode', JSON.stringify(compactMode));
  }, [compactMode]);

  // Save sidebar state
  useEffect(() => {
    localStorage.setItem('platformAdminSidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const changeColorScheme = (scheme) => {
    if (themePresets[scheme]) {
      setColorScheme(scheme);
    }
  };

  const toggleCompactMode = () => {
    setCompactMode(prev => !prev);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const resetToDefaults = () => {
    setDarkMode(false);
    setColorScheme('default');
    setCompactMode(false);
    setSidebarCollapsed(false);
    localStorage.removeItem('platformAdminDarkMode');
    localStorage.removeItem('platformAdminColorScheme');
    localStorage.removeItem('platformAdminCompactMode');
    localStorage.removeItem('platformAdminSidebarCollapsed');
  };

  const getCurrentTheme = () => {
    return themePresets[colorScheme] || themePresets.default;
  };

  const value = {
    // State
    darkMode,
    colorScheme,
    compactMode,
    sidebarCollapsed,
    
    // Actions
    toggleDarkMode,
    changeColorScheme,
    toggleCompactMode,
    toggleSidebar,
    resetToDefaults,
    
    // Helpers
    getCurrentTheme,
    themePresets,
  };

  return (
    <PlatformThemeContext.Provider value={value}>
      {children}
    </PlatformThemeContext.Provider>
  );
};
